import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Wind } from "lucide-react";

export default function Register() {
  const { register, error } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [purchaseBlock, setPurchaseBlock] = useState(null); // { checkoutUrl }
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await register(email, password, name);
    setLoading(false);
    if (result?.ok) { nav("/enroll"); return; }
    if (result?.purchaseRequired) {
      setPurchaseBlock({ checkoutUrl: result.checkoutUrl || "" });
    }
  };

  return (
    <div className="min-h-screen shell-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-navy flex items-center justify-center"><Wind size={20} className="text-gold" /></div>
          <div className="font-display font-bold text-navy text-xl">Breathe<span className="text-gold">·</span>Thrive</div>
        </Link>
        <div className="bg-warm-white border border-edge rounded-3xl p-8 shadow-[0_8px_30px_rgba(44,45,91,0.06)]">
          <h1 className="font-display text-2xl font-bold text-navy">Begin the journey</h1>
          <p className="text-sm text-ink-mid mt-1">Create a warm family space.</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Your name</label>
              <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none focus:ring-2 focus:ring-gold text-ink-dark" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Email</label>
              <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none focus:ring-2 focus:ring-gold text-ink-dark" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Password</label>
              <input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none focus:ring-2 focus:ring-gold text-ink-dark" />
              <div className="text-xs text-ink-soft mt-1">At least 6 characters</div>
            </div>
            {purchaseBlock ? (
              <div data-testid="register-purchase-required" className="text-sm bg-gold/10 border border-gold/40 rounded-xl px-4 py-3 space-y-3">
                <div className="font-display text-navy font-semibold">One quick step first</div>
                <div className="text-ink-mid">Breathe to Thrive is a paid program. Please complete your purchase — then come back here to create your account.</div>
                <a
                  data-testid="register-purchase-cta"
                  href={purchaseBlock.checkoutUrl || "https://www.breathingintolifeco.com/breathe-to-thrive"}
                  className="inline-flex items-center bg-navy text-warm-white px-5 py-2.5 rounded-full font-semibold hover:-translate-y-0.5 transition-all"
                >
                  Complete your purchase
                </a>
                <div className="text-xs text-ink-soft">Already paid within the last minute? Wait a moment for the confirmation and try again.</div>
              </div>
            ) : error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="register-error">{error}</div>}
            <button data-testid="register-submit" disabled={loading} className="w-full bg-gold text-ink-dark px-6 py-3 rounded-full font-semibold hover:bg-gold-light hover:-translate-y-0.5 transition-all disabled:opacity-50">
              {loading ? "Creating…" : "Create account"}
            </button>
          </form>
          <p className="mt-6 text-sm text-ink-mid text-center">
            Already here? <Link to="/login" className="text-navy font-semibold underline underline-offset-4">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
